// Prev/Next on a product page follow the grid the shopper came from.
//
// Shopify's collection.previous_product / next_product always walk the
// collection's default order, so after a sort, a filter or Hide sold the
// PDP's Prev/Next jumped to koi the shopper never saw. The collection grid
// (sections/main-collection-product-grid.liquid) lists the cards it shows,
// in the order it shows them (per-koi cards and the per-page price re-sort
// included), with its page number and the URLs of the grid pages either side.
// This script saves that list whenever the grid renders; a product page in
// the same collection finds itself in it and points Prev/Next
// (snippets/koi-nav.liquid) at its neighbours. At either end of a grid page
// it fetches the page beyond through the Section Rendering API and adds it
// to the list. Wherever no list reaches, the Liquid links (default order)
// stay as they are.
(() => {
  if (window.__CK_KOI_TRAIL__) return;
  window.__CK_KOI_TRAIL__ = true;

  const SESSION_PREFIX = 'ck-koi-trail:'; // this tab's recent lists, per collection
  const SHARED_KEY = 'ck-koi-trail'; // the latest grid in any tab, for koi opened in a new tab
  const MAX_TRAILS = 3; // per collection, so Back to a koi opened from an earlier view still finds it
  const SESSION_MAX_IDLE_MS = 2 * 60 * 60 * 1000; // an old list must not steer a tab left open for days
  const SHARED_MAX_IDLE_MS = 60 * 60 * 1000;
  const MAX_PAGE_FETCHES = 3; // per direction, per run
  const EDGE_WAIT_MS = 2500; // how long an edge tap waits for the page beyond before opening the grid page

  function readJSON(kind, key) {
    try {
      const raw = window[kind].getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null; // storage blocked or a malformed entry
    }
  }

  function writeJSON(kind, key, value) {
    try {
      window[kind].setItem(key, JSON.stringify(value));
    } catch {
      // storage blocked or full: product pages keep their default links
    }
  }

  function isFresh(trail, maxIdle) {
    return Date.now() - (trail.touchedAt || 0) < maxIdle;
  }

  // most recently used first
  function readTrails(handle) {
    const trails = readJSON('sessionStorage', SESSION_PREFIX + handle);
    return Array.isArray(trails) ? trails.filter((trail) => trail && Array.isArray(trail.entries)) : [];
  }

  function saveTrail(trail, { shared = false } = {}) {
    const others = readTrails(trail.handle).filter((other) => other.id !== trail.id);
    writeJSON('sessionStorage', SESSION_PREFIX + trail.handle, [trail, ...others].slice(0, MAX_TRAILS));
    if (shared) writeJSON('localStorage', SHARED_KEY, trail);
  }

  // The sort and filters a grid shows, as one canonical string (no page,
  // no tracking params, no empty price bounds; keys in order). Empty means
  // Shopify's default collection order.
  function viewQuery(search) {
    const kept = [];
    new URLSearchParams(search).forEach((value, key) => {
      if (value !== '' && (key === 'sort_by' || key.startsWith('filter.'))) kept.push(`${key}=${value}`);
    });
    return kept.sort().join('&');
  }

  // One grid page: its number, its cards in order, and the grid pages either
  // side. `grid` is #product-grid, on this page or in a fetched section.
  function readGrid(grid) {
    const page = Number(grid.dataset.page) || 0;
    const entries = [];
    const seenListings = new Set();
    grid.querySelectorAll(':scope > li[data-trail-key]').forEach((item) => {
      const link = item.querySelector('a[href*="/products/"]');
      if (!link) return;
      // A koi card of a group split only for Hide sold: it keeps its place,
      // but walks as its listing (see stepIndex)
      const listing = item.dataset.trailListing || '';
      // ck-card-koi-pick.js points a group card's link at whichever koi the
      // pointer is over, keeping the listing's own link aside
      const url = (link.dataset.ckListingHref || link.getAttribute('href')).split('#')[0];
      // what the card says: "2026SEPT-067 · Koi B" plus its variety line;
      // a listing stop is named for the listing, not one of its koi
      const code = item.querySelector('.koi-card__code');
      const name = item.querySelector('.koi-card__name');
      const title =
        code && !listing ? [code, name].filter(Boolean).map((el) => el.textContent).join(' ') : item.dataset.trailTitle || '';
      const entry = {
        key: item.dataset.trailKey,
        url,
        title: title.replace(/\s+/g, ' ').trim(),
        thumb: item.dataset.trailThumb || '',
        page,
      };
      if (listing) {
        entry.listing = listing;
        // a listing's cards all sit on one page; only its first is a stop
        if (seenListings.has(listing)) entry.repeat = true;
        seenListings.add(listing);
      }
      // a per-koi card: how to frame its fish within the group photo
      if (item.dataset.trailKoi) {
        entry.koi = {
          zoom: item.dataset.trailKoi,
          ar: Number(item.dataset.trailKoiAr) || 1,
          crop: item.hasAttribute('data-trail-koi-crop'),
          bg: item.dataset.trailKoiCrop || '',
        };
      }
      entries.push(entry);
    });
    return { page, entries, prevUrl: grid.dataset.prevUrl || '', nextUrl: grid.dataset.nextUrl || '' };
  }

  // Puts one grid page into a trail, in page order, replacing what the trail
  // held for that page. A koi posted or sold meanwhile can shift a card across
  // a page break: keepExisting keeps the copy already in the trail (a product
  // page mid-walk), otherwise the page just rendered wins.
  function mergePage(trail, page, keepExisting) {
    const others = trail.entries.filter((entry) => entry.page !== page.page);
    const taken = new Set((keepExisting ? others : page.entries).map((entry) => entry.key));
    const added = keepExisting ? page.entries.filter((entry) => !taken.has(entry.key)) : page.entries;
    const kept = keepExisting ? others : others.filter((entry) => !taken.has(entry.key));
    const at = kept.findIndex((entry) => entry.page > page.page);
    trail.entries = at < 0 ? kept.concat(added) : kept.slice(0, at).concat(added, kept.slice(at));
    trail.firstPage = Math.min(trail.firstPage, page.page);
    trail.lastPage = Math.max(trail.lastPage, page.page);
    if (page.page === trail.firstPage) trail.prevUrl = page.prevUrl;
    if (page.page === trail.lastPage) trail.nextUrl = page.nextUrl;
  }

  // ---- collection page: save the grid as shown ----

  function initCollectionPage() {
    const root = document.querySelector('collection-component[data-collection-handle]');
    if (!root) return;
    const handle = root.dataset.collectionHandle;
    let frozen = false;
    let lastId = '';

    const capture = () => {
      const grid = root.querySelector('#product-grid');
      if (!grid) return;
      const page = readGrid(grid);
      const query = viewQuery(location.search);
      const section = grid.dataset.id;
      // another page of a view already saved (pagination, the grid page an
      // edge link opened, Back to an earlier filter) joins that view's list
      let trail = readTrails(handle).find(
        (saved) =>
          isFresh(saved, SESSION_MAX_IDLE_MS) &&
          saved.query === query &&
          saved.section === section &&
          page.page > 0 &&
          page.page >= saved.firstPage - 1 &&
          page.page <= saved.lastPage + 1
      );
      if (trail) {
        mergePage(trail, page, false);
      } else if (!page.entries.length) {
        return; // nothing to walk: keep the lists already saved
      } else {
        trail = {
          id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
          handle,
          section,
          query,
          firstPage: page.page,
          lastPage: page.page,
          prevUrl: page.prevUrl,
          nextUrl: page.nextUrl,
          entries: page.entries,
        };
      }
      trail.touchedAt = Date.now();
      saveTrail(trail, { shared: true });
      lastId = trail.id;
    };

    capture();
    // sort, filter and Hide sold swap the grid in place (facets.js)
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(() => {
        if (!frozen) capture();
      }).observe(container, { childList: true });
    }

    const cardLink = (event) => event.target.closest('#product-grid a[href*="/products/"]');
    const onCardOpen = (event) => {
      const link = cardLink(event);
      if (!link) return;
      capture();
      // Opening in this tab: a filter response that lands before the page
      // unloads must not swap the list out from under the koi being opened.
      // If the navigation never happens (stopped, swiped back), thaw.
      const plain = event.type === 'click' && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      if (plain && link.target !== '_blank') {
        frozen = true;
        setTimeout(() => (frozen = false), 4000);
      }
    };
    root.addEventListener('click', onCardOpen, true);
    root.addEventListener('auxclick', onCardOpen, true); // middle click: new tab
    root.addEventListener('contextmenu', onCardOpen, true); // "Open in new tab"
    // iOS's long-press "Open in New Tab" fires none of those: make sure the
    // shared copy is this grid, and lists this card, as soon as it is touched
    root.addEventListener(
      'pointerdown',
      (event) => {
        const link = cardLink(event);
        const item = link && link.closest('li[data-trail-key]');
        if (!item) return;
        let shared = '';
        try {
          shared = localStorage.getItem(SHARED_KEY) || '';
        } catch {
          return; // storage blocked
        }
        if (!lastId || !shared.includes(`"id":"${lastId}"`) || !shared.includes(`"key":"${item.dataset.trailKey}"`)) capture();
      },
      { capture: true, passive: true }
    );

    // Back to the grid from the bfcache: the list may have moved on meanwhile
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      frozen = false;
      capture();
    });
  }

  // ---- product page: point Prev/Next at the grid neighbours ----

  function locate(trail, productId, variantId) {
    const keys = trail.entries.map((entry) => entry.key);
    // a per-koi card is listing-variant, a Hide-sold split card listing~variant,
    // a whole-listing card the listing alone
    let index = variantId ? keys.indexOf(`${productId}-${variantId}`) : -1;
    if (index < 0 && variantId) index = keys.indexOf(`${productId}~${variantId}`);
    if (index < 0) index = keys.indexOf(productId);
    if (index < 0) index = keys.findIndex((key) => key.startsWith(`${productId}-`) || key.startsWith(`${productId}~`));
    return index;
  }

  // Where Prev (-1) or Next (1) lands, or -1 past the loaded cards. Koi cards
  // of a group split only for Hide sold (entry.listing) walk as their
  // listing: skip the current listing's other cards, and any later card of
  // a listing already passed (its first card is its stop).
  function stepIndex(state, step) {
    const { entries } = state.trail;
    const here = entries[state.index];
    for (let i = state.index + step; i >= 0 && i < entries.length; i += step) {
      const entry = entries[i];
      if (entry.listing && (entry.repeat || (here && entry.listing === here.listing))) continue;
      return i;
    }
    return -1;
  }

  // preferId: the list this page was walking before a reload or a Back
  // (history.state), which wins over a newer view that also lists this koi
  function findTrail(handle, productId, variantId, preferId) {
    const trails = readTrails(handle).filter((trail) => isFresh(trail, SESSION_MAX_IDLE_MS));
    trails.sort((a, b) => (b.id === preferId) - (a.id === preferId));
    for (const trail of trails) {
      const index = locate(trail, productId, variantId);
      if (index >= 0) return { trail, index };
    }
    // Opened in a new tab (session storage starts empty) or from a grid in
    // another tab: follow the latest grid seen anywhere, if it lists this koi.
    const shared = readJSON('localStorage', SHARED_KEY);
    if (!shared || shared.handle !== handle || !Array.isArray(shared.entries) || !isFresh(shared, SHARED_MAX_IDLE_MS)) return null;
    const index = locate(shared, productId, variantId);
    if (index < 0) return null;
    saveTrail(shared);
    return { trail: shared, index };
  }

  // The stored copy of this page's list may have grown since the page read
  // it (later product pages fetched more grid pages, then Back restored this
  // one from the bfcache): take those pages in rather than write over them.
  function absorbStored(state) {
    const mine = state.trail;
    const stored = readTrails(mine.handle).find((trail) => trail.id === mine.id);
    if (!stored || stored.lastPage < mine.firstPage - 1 || stored.firstPage > mine.lastPage + 1) return;
    const pageOf = (number) => ({
      page: number,
      entries: stored.entries.filter((entry) => entry.page === number),
      prevUrl: number === stored.firstPage ? stored.prevUrl : '',
      nextUrl: number === stored.lastPage ? stored.nextUrl : '',
    });
    for (let number = mine.lastPage + 1; number <= stored.lastPage; number += 1) mergePage(mine, pageOf(number), true);
    for (let number = mine.firstPage - 1; number >= stored.firstPage; number -= 1) mergePage(mine, pageOf(number), true);
    state.index = mine.entries.findIndex((entry) => entry.key === state.key);
  }

  function persist(state) {
    absorbStored(state);
    const { trail } = state;
    trail.touchedAt = Date.now();
    saveTrail(trail);
    // keep the shared copy in step, unless another tab has since grown it further
    const shared = readJSON('localStorage', SHARED_KEY);
    if (shared && shared.id === trail.id && Array.isArray(shared.entries) && shared.entries.length <= trail.entries.length) {
      writeJSON('localStorage', SHARED_KEY, trail);
    }
  }

  // The link for one side: the neighbouring card; or, when that card is on a
  // grid page not fetched yet, the grid page itself until it arrives.
  function neighbour(state, step, defaults) {
    const { trail } = state;
    const at = stepIndex(state, step);
    const entry = at < 0 ? null : trail.entries[at];
    const word = step < 0 ? 'Previous' : 'Next';
    if (entry) {
      return {
        url: `${entry.url}#main-product`,
        label: entry.title ? `${word} listing: ${entry.title}` : `${word} listing`,
        thumb: entry.thumb,
        koi: entry.koi || null,
      };
    }
    const pageUrl = step < 0 ? trail.prevUrl : trail.nextUrl;
    if (!pageUrl) return null;
    // unsorted and unfiltered, the grid is Shopify's own collection order,
    // so the Liquid link is already the koi beyond the page break
    if (!trail.query) return step < 0 ? defaults.prev : defaults.next;
    return { url: `${pageUrl}#ProductGridContainer`, label: `${word} page of listings`, thumb: '', page: true };
  }

  function readLink(link) {
    if (!link || link.hidden || !link.getAttribute('href')) return null;
    const wrap = link.querySelector('.koi-nav__thumb-wrap');
    const img = wrap && wrap.querySelector('.koi-nav__thumb');
    return {
      url: link.getAttribute('href'),
      label: link.getAttribute('aria-label') || '',
      thumb: wrap && !wrap.hidden && img ? img.getAttribute('src') || '' : '',
    };
  }

  function paintLink(link, to) {
    if (!link) return;
    link.hidden = !to;
    link.toggleAttribute('data-trail-page', Boolean(to && to.page));
    if (!to) {
      link.removeAttribute('href');
      return;
    }
    link.setAttribute('href', to.url);
    link.setAttribute('aria-label', to.label);
    const wrap = link.querySelector('.koi-nav__thumb-wrap');
    const img = wrap && wrap.querySelector('.koi-nav__thumb');
    if (!wrap || !img) return;
    // One koi of a group photo: frame that fish (its crop, or a zoom onto
    // its letter) instead of showing the whole group photo
    const koi = wrap.querySelector('.koi-nav__koi');
    const frame = koi && koi.querySelector('.koi-nav__koi-frame');
    const koiImg = frame && frame.querySelector('img');
    const showKoi = Boolean(to.thumb && to.koi && koiImg);
    if (showKoi) {
      frame.setAttribute('style', to.koi.zoom);
      frame.style.setProperty('--koi-ar', String(to.koi.ar));
      koi.classList.toggle('koi-nav__koi--letter', !to.koi.crop);
      if (to.koi.crop && to.koi.bg) koi.style.setProperty('--koi-crop-bg', to.koi.bg);
      else koi.style.removeProperty('--koi-crop-bg');
      if (koiImg.getAttribute('src') !== to.thumb) koiImg.setAttribute('src', to.thumb);
    } else if (to.thumb && img.getAttribute('src') !== to.thumb) {
      img.setAttribute('src', to.thumb);
    }
    if (koi) koi.hidden = !showKoi;
    img.hidden = showKoi;
    wrap.hidden = !to.thumb;
  }

  function paint(navs, prev, next) {
    navs.forEach((nav) => {
      paintLink(nav.querySelector('.koi-nav__link--prev'), prev);
      paintLink(nav.querySelector('.koi-nav__link--next'), next);
      const divider = nav.querySelector('.koi-nav__divider');
      if (divider) divider.hidden = !(prev && next);
      nav.classList.toggle('koi-nav--single', !(prev && next));
      nav.hidden = !prev && !next;
    });
  }

  async function fetchPage(section, url) {
    try {
      const join = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${join}section_id=${encodeURIComponent(section)}`, { priority: 'low' });
      if (!response.ok) return null;
      const html = await response.text();
      // parse only the grid onwards: the facets ahead of it are most of the response
      const start = html.search(/<(?:ul|div)\s[^>]*\bid="product-grid"/);
      if (start < 0) return null;
      const grid = new DOMParser().parseFromString(html.slice(start), 'text/html').getElementById('product-grid');
      return grid ? readGrid(grid) : null;
    } catch {
      return null;
    }
  }

  function initProductPage() {
    const navs = document.querySelectorAll('.koi-nav[data-koi-trail]');
    if (!navs.length) return;
    const { koiTrail: handle, productId } = navs[0].dataset;
    // the koi this page was opened on (a koi switch rewrites the URL later)
    const variantId = new URLSearchParams(location.search).get('variant');
    const found = findTrail(handle, productId, variantId, history.state && history.state.ckTrail);
    document.documentElement.classList.remove('ck-trail-pending');
    if (!found) return; // the Liquid links (default order) stay

    // what Liquid rendered: Shopify's order, right for an unsorted, unfiltered grid
    const defaults = {
      prev: readLink(navs[0].querySelector('.koi-nav__link--prev')),
      next: readLink(navs[0].querySelector('.koi-nav__link--next')),
    };
    const state = { trail: found.trail, index: found.index, key: found.trail.entries[found.index].key };
    // A reload or Back to this entry resumes on the card it was on: after a
    // koi switch the URL names another koi, whose Hide-sold card can sit
    // elsewhere in a price-sorted grid
    const saved = history.state;
    if (saved && saved.ckTrail === state.trail.id && saved.ckTrailKey) {
      const at = state.trail.entries.findIndex((entry) => entry.key === saved.ckTrailKey);
      const mine = (key) => key === productId || key.startsWith(`${productId}-`) || key.startsWith(`${productId}~`);
      if (at >= 0 && mine(saved.ckTrailKey)) {
        state.index = at;
        state.key = saved.ckTrailKey;
      }
    }
    // Entered a listing at a later koi card of a Hide-sold split: that card
    // is the listing's stop for this walk, so Next then Prev comes back here
    const here = state.trail.entries[state.index];
    let restopped = false;
    if (here.listing && here.repeat) {
      state.trail.entries.forEach((entry) => {
        if (entry.listing === here.listing) entry.repeat = entry !== here;
      });
      restopped = true;
    }
    const repaint = () => paint(navs, neighbour(state, -1, defaults), neighbour(state, 1, defaults));
    // tag this history entry with its list and card, so a reload or Back walks the same one
    const remember = () => {
      try {
        history.replaceState({ ...(history.state || {}), ckTrail: state.trail.id, ckTrailKey: state.key }, '');
      } catch {
        // history unavailable: findTrail falls back to the most recent list
      }
    };
    repaint();
    remember();
    if (restopped || Date.now() - state.trail.touchedAt > 60 * 1000) persist(state);

    let pending = null;
    let rerun = false;
    const needsPage = (step) => {
      const { trail } = state;
      return Boolean(trail.query && (step < 0 ? trail.prevUrl : trail.nextUrl) && stepIndex(state, step) < 0);
    };
    const extend = () => {
      if (pending) {
        rerun = true; // the koi moved mid-fetch: check its edges again afterwards
        return;
      }
      if (!(needsPage(1) || needsPage(-1))) return;
      pending = (async () => {
        for (const step of [1, -1]) {
          for (let fetches = 0; fetches < MAX_PAGE_FETCHES && needsPage(step); fetches += 1) {
            const page = await fetchPage(state.trail.section, step < 0 ? state.trail.prevUrl : state.trail.nextUrl);
            if (!page || !page.page) break;
            mergePage(state.trail, page, true);
            state.index = state.trail.entries.findIndex((entry) => entry.key === state.key);
            persist(state);
            repaint();
          }
        }
      })().finally(() => {
        pending = null;
        if (rerun) {
          rerun = false;
          extend();
        }
      });
    };
    extend();

    // Any Prev/Next tap: the page it opens should walk this same list. An
    // edge link still on the grid page waits a moment for the koi beyond it
    // rather than drop the shopper back on the grid.
    // The neighbour is another koi of this same listing (per-koi cards side by
    // side): pick it here, the way a koi pill does, instead of reloading the page
    const selectHere = (link) => {
      const step = link.classList.contains('koi-nav__link--prev') ? -1 : 1;
      const index = stepIndex(state, step);
      const entry = index < 0 ? null : state.trail.entries[index];
      if (!entry || link.hasAttribute('data-trail-page') || !entry.key.startsWith(`${productId}-`)) return false;
      const variant = entry.key.slice(productId.length + 1);
      const picker = document.querySelector(`product-info[data-product-id="${productId}"] variant-selects`);
      if (!picker) return false;
      const radios = picker.querySelectorAll('input[type="radio"]');
      const radio = picker.querySelector(`input[type="radio"][data-variant-id="${variant}"]`);
      // one option (the koi letter): that radio alone picks this koi
      if (!radio || new Set([...radios].map((input) => input.name)).size !== 1) return false;
      // The radio, not the koi pill: main-product syncs the pills on this
      // change, and a scripted pill click would log a pill tap in analytics.
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));

      // product-info.js focuses the picked radio once its update lands; put
      // focus back on Prev/Next, or the next Enter would add this koi to the
      // cart and an arrow key would switch koi
      const nav = link.closest('.koi-nav');
      const stop = () => document.removeEventListener('focusin', refocus, true);
      const refocus = (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('variant-selects')) return;
        stop();
        const to = link.hidden ? nav.querySelector('.koi-nav__link:not([hidden])') : link;
        if (to && !nav.hidden) to.focus({ preventScroll: true });
        else event.target.blur();
      };
      document.addEventListener('focusin', refocus, true);
      setTimeout(stop, 8000);

      // move along now, so a quick second tap goes on to the koi after it
      state.index = index;
      state.key = entry.key;
      remember();
      repaint();
      extend();
      return true;
    };

    let tapToken = 0;
    navs.forEach((nav) =>
      nav.addEventListener('click', (event) => {
        const link = event.target.closest('.koi-nav__link');
        if (!link) return;
        tapToken += 1; // a later tap cancels an edge tap still waiting
        persist(state);
        const modified = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
        if (!modified && selectHere(link)) {
          event.preventDefault();
          return;
        }
        if (!link.hasAttribute('data-trail-page') || !pending || modified) return;
        event.preventDefault();
        const token = tapToken;
        link.setAttribute('aria-busy', 'true'); // dims it: the tap registered
        const wait = new Promise((resolve) => setTimeout(resolve, EDGE_WAIT_MS));
        Promise.race([pending, wait]).then(() => {
          const href = link.getAttribute('href');
          if (token !== tapToken || !href || link.hidden) {
            // cancelled, or the page beyond had no koi after all: stay here
            link.removeAttribute('aria-busy');
            if (link.hidden && document.activeElement === document.body) {
              const other = nav.querySelector('.koi-nav__link:not([hidden])');
              if (other) other.focus();
            }
            return;
          }
          window.location.assign(href);
          // still here a while later (the navigation was stopped): usable again
          setTimeout(() => link.removeAttribute('aria-busy'), 5000);
        });
      })
    );
    window.addEventListener('pagehide', () => {
      tapToken += 1;
    });

    // A koi switch on a split listing moves to that koi's own card, so Next
    // doesn't just reload the koi already on screen. product-info.js resets
    // history.state when it rewrites the URL, so tag the entry again.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        remember();
        const variant = event && event.data && event.data.variant;
        if (!variant) return;
        const index = state.trail.entries.findIndex((entry) => entry.key === `${productId}-${variant.id}`);
        if (index < 0 || index === state.index) return;
        state.index = index;
        state.key = state.trail.entries[index].key;
        remember();
        repaint();
        extend();
      });
    }

    // Back or Forward to this page from the bfcache keeps the links it had;
    // only an edge tap's wait and an unfinished page fetch need undoing.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      navs.forEach((nav) => nav.querySelectorAll('[aria-busy]').forEach((link) => link.removeAttribute('aria-busy')));
      absorbStored(state);
      repaint();
      extend();
    });
  }

  initCollectionPage();
  initProductPage();
})();
